<script setup>
// HomeCampaignAppPage.vue - Home page for WhatsApp App campaigns
import { computed, onMounted, onActivated, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import { Button as ShadcnButton } from 'dashboard/components-shadcn/new-york/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'dashboard/components-shadcn/new-york/ui/select';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useVueTable,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import whatsappIcon from 'dashboard/assets/images/whatsapp.png';
import { useAlert } from 'dashboard/composables';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

// Reactive state for sorting
const sorting = ref([]);

// Reactive state for pagination
const pagination = ref({
  pageIndex: 0,
  pageSize: 10,
});

// Computed: Channel::Whatsapp inboxes with provider 'baileys' (WhatsApp App)
const whatsappAppInboxes = computed(() => {
  const inboxes = store.getters['inboxes/getInboxes'];
  if (!inboxes || !Array.isArray(inboxes)) {
    return [];
  }
  return inboxes.filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' && inbox.provider === 'baileys'
  );
});

// Computed: templates from whatsappAppTemplates store
const templates = computed(() => {
  return store.getters['whatsappAppTemplates/getTemplates'] || [];
});

// Computed: UI flags
const isFetching = computed(() => {
  return store.getters['whatsappAppTemplates/getUIFlags']?.isFetching || false;
});

const isDeleting = computed(() => {
  return store.getters['whatsappAppTemplates/getUIFlags']?.isDeleting || false;
});

// Helper: check if template has interactive config
const isInteractive = template => {
  return !!(
    template.interactive_config &&
    Object.keys(template.interactive_config).length > 0
  );
};

// Helper: truncate content for preview
const truncateContent = (content, maxLength = 80) => {
  if (!content) return '';
  return content.length > maxLength
    ? content.substring(0, maxLength) + '...'
    : content;
};

// Helper: get inbox name from inbox_id
const getInboxName = inboxId => {
  if (!inboxId) return '';
  const inboxes = store.getters['inboxes/getInboxes'];
  const inbox = inboxes?.find(i => i.id === inboxId);
  return inbox?.name || '';
};

// Table columns
const columns = [
  {
    accessorKey: 'name',
    header: () => t('CAMPAIGN.WHATSAPP_APP.HOME.TABLE.NAME'),
    enableSorting: true,
  },
  {
    id: 'inbox',
    header: () => t('CAMPAIGN.WHATSAPP_APP.HOME.TABLE.INBOX'),
    enableSorting: true,
    accessorFn: row => getInboxName(row.inbox_id),
  },
  {
    id: 'content',
    header: () => t('CAMPAIGN.WHATSAPP_APP.HOME.TABLE.CONTENT'),
    enableSorting: false,
    accessorFn: row => row.content,
  },
  {
    id: 'interactive',
    header: () => t('CAMPAIGN.WHATSAPP_APP.HOME.TABLE.INTERACTIVE'),
    enableSorting: true,
    accessorFn: row => isInteractive(row),
  },
  {
    id: 'actions',
    header: () => t('CAMPAIGN.WHATSAPP_APP.HOME.TABLE.ACTIONS'),
    enableSorting: false,
    accessorFn: () => null,
  },
];

// Table instance
const table = useVueTable({
  get data() {
    return templates.value || [];
  },
  columns,
  state: {
    get sorting() {
      return sorting.value;
    },
    get pagination() {
      return pagination.value;
    },
  },
  onSortingChange: updater => {
    sorting.value =
      typeof updater === 'function' ? updater(sorting.value) : updater;
    if (!Array.isArray(sorting.value)) {
      sorting.value = [];
    }
  },
  onPaginationChange: updater => {
    pagination.value =
      typeof updater === 'function' ? updater(pagination.value) : updater;
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
});

// Handle "Criar Transmissao"
const handleSendBroadcast = async template => {
  try {
    await store.dispatch('campaigns/clearCampaignData');
    await store.dispatch('campaigns/clearWhatsappNormalCampaignData');

    const campaignData = {
      template: JSON.parse(JSON.stringify(template)),
      templateId: template.id,
      templateName: template.name,
      inboxId: template.inbox_id,
    };

    await store.dispatch('campaigns/saveCampaignData', campaignData);

    router.push({
      name: 'campaigns_whatsapp_app_home_create',
      query: {
        templateName: template.name,
      },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error setting up campaign:', error);
  }
};

// Handle delete
const handleDelete = async template => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`${t('CAMPAIGN.CONFIRM_DELETE.TITLE')}`)) {
    return;
  }
  try {
    await store.dispatch('whatsappAppTemplates/delete', template.id);
    useAlert(t('CAMPAIGN.WHATSAPP_APP.HOME.ALERTS.DELETE_SUCCESS'));
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP_APP.HOME.ALERTS.DELETE_ERROR'));
  }
};

// Navigate to inbox setup
const redirectToInboxSetup = () => {
  router.push({
    name: 'settings_inboxes_page_channel',
    params: { sub_page: 'whatsapp' },
    query: { provider: 'whatsapp_baileys' },
  });
};

// Navigate to template builder
const redirectToTemplates = () => {
  router.push({
    name: 'campaigns_whatsapp_app_templates',
  });
};

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('whatsappAppTemplates/fetch');
});

// Refetch ao retornar via KeepAlive (ex: criou template e voltou para esta página)
onActivated(() => {
  store.dispatch('whatsappAppTemplates/fetch');
});
</script>

<template>
  <div class="whatsapp-templates-page">
    <!-- WhatsApp Header -->
    <header class="whatsapp-header">
      <div class="header-background">
        <div class="header-content">
          <div class="header-info">
            <div class="header-icon">
              <img
                :src="whatsappIcon"
                alt="WhatsApp App"
                class="whatsapp-icon"
              />
            </div>
            <div class="header-text">
              <h1 class="header-title">
                {{ $t('CAMPAIGN.WHATSAPP_APP.HOME.HEADER_TITLE') }}
              </h1>
              <p class="header-description">
                {{ $t('CAMPAIGN.WHATSAPP_APP.HOME.GUIDELINE_TEXT') }}
              </p>
            </div>
          </div>

          <!-- Header Stats -->
          <div class="header-stats">
            <!-- Skeleton Loading for Stats -->
            <div v-if="isFetching" class="stats-skeleton">
              <div class="stat-card-skeleton">
                <div class="stat-number-skeleton" />
                <div class="stat-label-skeleton" />
              </div>
              <div class="stat-card-skeleton">
                <div class="stat-number-skeleton" />
                <div class="stat-label-skeleton" />
              </div>
            </div>

            <!-- Real Stats -->
            <div v-else class="header-stats-real">
              <div class="stat-card">
                <div class="stat-number">
                  {{ templates?.length || 0 }}
                </div>
                <div class="stat-label">
                  {{ $t('CAMPAIGN.WHATSAPP_APP.HOME.STATS.TEMPLATES') }}
                </div>
              </div>
              <div class="stat-card">
                <div class="stat-number">
                  {{ whatsappAppInboxes?.length || 0 }}
                </div>
                <div class="stat-label">
                  {{ $t('CAMPAIGN.WHATSAPP_APP.HOME.STATS.INBOXES') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="page-content">
      <!-- Skeleton Loading State -->
      <div v-if="isFetching" class="skeleton-container">
        <div class="skeleton-content">
          <div class="skeleton-table-wrapper">
            <div class="skeleton-table-header">
              <div v-for="i in 5" :key="i" class="skeleton-header-cell">
                <div class="skeleton-header-text" />
              </div>
            </div>
            <div class="skeleton-table-body">
              <div v-for="row in 6" :key="row" class="skeleton-table-row">
                <div v-for="col in 5" :key="col" class="skeleton-table-cell">
                  <div v-if="col === 1" class="skeleton-template-name">
                    <div class="skeleton-icon" />
                    <div class="skeleton-text-primary" />
                  </div>
                  <div v-else-if="col === 2" class="skeleton-category">
                    <div class="skeleton-text-secondary w-32" />
                  </div>
                  <div v-else-if="col === 3" class="skeleton-category">
                    <div class="skeleton-badge" />
                  </div>
                  <div v-else class="skeleton-actions">
                    <div class="skeleton-action-btn" />
                    <div class="skeleton-action-btn" />
                  </div>
                </div>
              </div>
            </div>
            <div class="skeleton-pagination">
              <div class="skeleton-pagination-info">
                <div class="skeleton-text-info" />
              </div>
              <div class="skeleton-pagination-controls">
                <div v-for="i in 4" :key="i" class="skeleton-pagination-btn" />
              </div>
            </div>
          </div>
          <div class="skeleton-status">
            <div class="skeleton-pulse-dot" />
            <span class="skeleton-status-text">
              {{ $t('CAMPAIGN.WHATSAPP_APP.HOME.LOADING') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Templates Table -->
      <div
        v-else-if="templates && templates.length > 0"
        class="templates-container fade-in"
      >
        <div class="table-wrapper">
          <table class="whatsapp-table">
            <thead>
              <tr
                v-for="headerGroup in table.getHeaderGroups()"
                :key="headerGroup.id"
              >
                <th
                  v-for="header in headerGroup.headers"
                  :key="header.id"
                  :class="{
                    'cursor-pointer select-none': header.column.getCanSort(),
                  }"
                  @click="
                    header.column.getCanSort()
                      ? header.column.getToggleSortingHandler()?.($event)
                      : undefined
                  "
                >
                  <div class="flex items-center gap-2">
                    <span>{{
                      header.isPlaceholder
                        ? ''
                        : typeof header.column.columnDef.header === 'function'
                          ? header.column.columnDef.header()
                          : header.column.columnDef.header
                    }}</span>
                    <span v-if="header.column.getCanSort()">
                      <span
                        v-if="header.column.getIsSorted() === 'asc'"
                        class="i-lucide-arrow-up w-3.5 h-3.5"
                      />
                      <span
                        v-else-if="header.column.getIsSorted() === 'desc'"
                        class="i-lucide-arrow-down w-3.5 h-3.5"
                      />
                      <span
                        v-else
                        class="i-lucide-arrow-up-down w-3.5 h-3.5 opacity-40"
                      />
                    </span>
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="row in table.getRowModel().rows"
                :key="row.id"
                class="hover:bg-slate-50 dark:hover:bg-green-900/10"
              >
                <td
                  v-for="cell in row.getVisibleCells()"
                  :key="cell.id"
                  class="p-3 border-b border-green-900/30 dark:border-green-900/30"
                >
                  <!-- Name Cell -->
                  <div v-if="cell.column.id === 'name'" class="template-name">
                    <span class="template-text">{{ cell.getValue() }}</span>
                  </div>

                  <!-- Inbox Cell -->
                  <div
                    v-else-if="cell.column.id === 'inbox'"
                    class="template-name"
                  >
                    <span
                      v-if="cell.getValue()"
                      class="text-sm text-slate-600 dark:text-slate-400"
                    >
                      {{ cell.getValue() }}
                    </span>
                    <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                    <span v-else class="text-xs text-n-slate-9 italic">
                      &mdash;
                    </span>
                  </div>

                  <!-- Content Preview Cell -->
                  <div
                    v-else-if="cell.column.id === 'content'"
                    class="content-cell"
                  >
                    <span
                      class="content-text text-slate-600 dark:text-slate-400"
                    >
                      {{ truncateContent(row.original.content) }}
                    </span>
                  </div>

                  <!-- Interactive Type Cell -->
                  <span
                    v-else-if="cell.column.id === 'interactive'"
                    class="status-badge"
                    :class="
                      cell.getValue() ? 'status-approved' : 'status-default'
                    "
                  >
                    {{
                      cell.getValue()
                        ? $t('CAMPAIGN.WHATSAPP_APP.HOME.TYPE.INTERACTIVE')
                        : $t('CAMPAIGN.WHATSAPP_APP.HOME.TYPE.TEXT_ONLY')
                    }}
                  </span>

                  <!-- Actions Cell -->
                  <div
                    v-else-if="cell.column.id === 'actions'"
                    class="actions-cell"
                  >
                    <Button
                      v-tooltip.top="
                        $t('CAMPAIGN.WHATSAPP_APP.HOME.BUTTONS.SEND_BROADCAST')
                      "
                      variant="faded"
                      color="slate"
                      size="xs"
                      icon="i-lucide-sliders-vertical"
                      @click="handleSendBroadcast(row.original)"
                    />
                    <Button
                      v-tooltip.top="
                        $t('CAMPAIGN.WHATSAPP_APP.HOME.BUTTONS.DELETE')
                      "
                      variant="faded"
                      color="ruby"
                      size="xs"
                      icon="i-lucide-trash"
                      :disabled="isDeleting"
                      @click="handleDelete(row.original)"
                    />
                  </div>

                  <!-- Default Cell -->
                  <span v-else>{{ cell.getValue() }}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination -->
          <div class="pagination-wrapper">
            <div
              class="flex items-center justify-between px-2 pagination-container"
            >
              <div class="flex-1 text-sm text-n-slate-10">
                <span v-if="table.getFilteredRowModel().rows.length > 0">
                  {{
                    $t('CAMPAIGN.WHATSAPP_APP.HOME.PAGINATION.SHOWING', {
                      from:
                        table.getState().pagination.pageIndex *
                          table.getState().pagination.pageSize +
                        1,
                      to: Math.min(
                        (table.getState().pagination.pageIndex + 1) *
                          table.getState().pagination.pageSize,
                        table.getFilteredRowModel().rows.length
                      ),
                      total: table
                        .getFilteredRowModel()
                        .rows.length.toLocaleString(),
                    })
                  }}
                </span>
                <span v-else>
                  {{ $t('CAMPAIGN.WHATSAPP_APP.HOME.PAGINATION.NO_RECORDS') }}
                </span>
              </div>

              <div
                class="flex items-center space-x-6 lg:space-x-8 pagination-controls"
              >
                <div class="flex items-center space-x-2">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{
                      $t('CAMPAIGN.WHATSAPP_APP.HOME.PAGINATION.ROWS_PER_PAGE')
                    }}
                  </p>
                  <Select
                    :model-value="`${table.getState().pagination.pageSize}`"
                    @update:model-value="
                      value => table.setPageSize(Number(value))
                    "
                  >
                    <SelectTrigger
                      class="h-8 w-[70px] bg-n-slate-4 border-n-slate-6 text-n-slate-12"
                    >
                      <SelectValue
                        :placeholder="`${table.getState().pagination.pageSize}`"
                      />
                    </SelectTrigger>
                    <SelectContent
                      side="top"
                      class="bg-n-slate-1 border-n-slate-6"
                    >
                      <SelectItem
                        v-for="pageSize in [5, 10, 20, 30, 50]"
                        :key="pageSize"
                        :value="`${pageSize}`"
                      >
                        {{ pageSize }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div
                  class="flex items-center justify-center text-sm font-medium text-n-slate-12 page-info whitespace-nowrap px-3"
                >
                  {{
                    $t('CAMPAIGN.WHATSAPP_APP.HOME.PAGINATION.PAGE_INFO', {
                      current: table.getState().pagination.pageIndex + 1,
                      total: table.getPageCount() || 1,
                    })
                  }}
                </div>

                <div class="flex items-center space-x-2">
                  <ShadcnButton
                    variant="outline"
                    class="hidden h-8 w-8 p-0 lg:flex border-n-slate-6 hover:bg-n-slate-3"
                    :disabled="!table.getCanPreviousPage()"
                    :title="
                      $t('CAMPAIGN.WHATSAPP_APP.HOME.PAGINATION.FIRST_PAGE')
                    "
                    @click="table.setPageIndex(0)"
                  >
                    <span class="sr-only">
                      {{
                        $t('CAMPAIGN.WHATSAPP_APP.HOME.PAGINATION.GO_TO_FIRST')
                      }}
                    </span>
                    <span
                      class="i-lucide-chevrons-left h-4 w-4 text-n-slate-11"
                    />
                  </ShadcnButton>

                  <ShadcnButton
                    variant="outline"
                    class="h-8 w-8 p-0 border-n-slate-6 hover:bg-n-slate-3"
                    :disabled="!table.getCanPreviousPage()"
                    :title="
                      $t('CAMPAIGN.WHATSAPP_APP.HOME.PAGINATION.PREVIOUS_PAGE')
                    "
                    @click="table.previousPage()"
                  >
                    <span class="sr-only">
                      {{
                        $t(
                          'CAMPAIGN.WHATSAPP_APP.HOME.PAGINATION.GO_TO_PREVIOUS'
                        )
                      }}
                    </span>
                    <span
                      class="i-lucide-chevron-left h-4 w-4 text-n-slate-11"
                    />
                  </ShadcnButton>

                  <ShadcnButton
                    variant="outline"
                    class="h-8 w-8 p-0 border-n-slate-6 hover:bg-n-slate-3"
                    :disabled="!table.getCanNextPage()"
                    :title="
                      $t('CAMPAIGN.WHATSAPP_APP.HOME.PAGINATION.NEXT_PAGE')
                    "
                    @click="table.nextPage()"
                  >
                    <span class="sr-only">
                      {{
                        $t('CAMPAIGN.WHATSAPP_APP.HOME.PAGINATION.GO_TO_NEXT')
                      }}
                    </span>
                    <span
                      class="i-lucide-chevron-right h-4 w-4 text-n-slate-11"
                    />
                  </ShadcnButton>

                  <ShadcnButton
                    variant="outline"
                    class="hidden h-8 w-8 p-0 lg:flex border-n-slate-6 hover:bg-n-slate-3"
                    :disabled="!table.getCanNextPage()"
                    :title="
                      $t('CAMPAIGN.WHATSAPP_APP.HOME.PAGINATION.LAST_PAGE')
                    "
                    @click="table.setPageIndex(table.getPageCount() - 1)"
                  >
                    <span class="sr-only">
                      {{
                        $t('CAMPAIGN.WHATSAPP_APP.HOME.PAGINATION.GO_TO_LAST')
                      }}
                    </span>
                    <span
                      class="i-lucide-chevrons-right h-4 w-4 text-n-slate-11"
                    />
                  </ShadcnButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-container fade-in">
        <!-- No Channel::Api inbox -->
        <div
          v-if="!whatsappAppInboxes || !whatsappAppInboxes.length"
          class="empty-state-card"
        >
          <div class="empty-icon">
            <span class="i-lucide-smartphone w-12 h-12 text-green-600" />
          </div>
          <h3 class="empty-title">
            {{ $t('CAMPAIGN.WHATSAPP_APP.HOME.EMPTY.NO_INBOX_TITLE') }}
          </h3>
          <p class="empty-description">
            {{ $t('CAMPAIGN.WHATSAPP_APP.HOME.EMPTY.NO_INBOX_DESCRIPTION') }}
          </p>
          <div class="empty-actions">
            <button class="btn-primary" @click="redirectToInboxSetup">
              {{ $t('CAMPAIGN.WHATSAPP_APP.HOME.EMPTY.SETUP_INBOX') }}
            </button>
          </div>
        </div>
        <!-- Has inbox but no templates -->
        <div v-else class="empty-state-card">
          <div class="empty-icon">
            <span class="i-lucide-file-text w-12 h-12 text-green-600" />
          </div>
          <h3 class="empty-title">
            {{ $t('CAMPAIGN.WHATSAPP_APP.HOME.EMPTY.NO_TEMPLATES_TITLE') }}
          </h3>
          <p class="empty-description">
            {{
              $t('CAMPAIGN.WHATSAPP_APP.HOME.EMPTY.NO_TEMPLATES_DESCRIPTION')
            }}
          </p>
          <div class="empty-actions">
            <button class="btn-primary" @click="redirectToTemplates">
              {{ $t('CAMPAIGN.WHATSAPP_APP.HOME.EMPTY.GO_TO_TEMPLATES') }}
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<style scoped>
.whatsapp-templates-page {
  @apply bg-n-background min-h-screen;
}

/* WhatsApp Header */
.whatsapp-header {
  @apply relative overflow-hidden;
}

.header-background {
  @apply bg-green-900/95;
}

.header-content {
  @apply mx-auto px-4 py-4 flex items-center justify-between;
}

.header-info {
  @apply flex items-center gap-4;
}

.whatsapp-icon {
  @apply w-10 h-10 object-contain drop-shadow-sm;
}

.header-text {
  @apply flex flex-col gap-2;
}

.header-title {
  @apply text-3xl font-bold text-white m-0;
}

.header-description {
  @apply text-green-100 dark:text-green-200 max-w-2xl m-0 text-lg;
}

.header-stats {
  @apply flex gap-4;
}

.header-stats-real {
  @apply flex gap-4;
}

/* Stats Skeleton */
.stats-skeleton {
  @apply flex gap-4;
}

.stat-card-skeleton {
  @apply bg-green-800/50 rounded-xl px-6 py-4 text-center w-24 h-20;
}

.stat-number-skeleton {
  @apply h-6 bg-gradient-to-r from-green-600 via-green-500 to-green-600 rounded mb-2;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.stat-label-skeleton {
  @apply h-3 bg-gradient-to-r from-green-600 via-green-500 to-green-600 rounded;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.stat-card {
  @apply bg-green-900 dark:bg-green-800 backdrop-blur-sm rounded-xl px-6 py-4 text-center relative;
}

.stat-number {
  @apply text-2xl font-bold text-white;
}

.stat-label {
  @apply text-sm text-green-100 dark:text-green-200;
}

/* Main Content */
.page-content {
  @apply px-0 mx-auto mt-1;
}

/* Skeleton Container */
.skeleton-container {
  @apply px-3 p-2;
}

.skeleton-content {
  @apply space-y-6;
}

/* Table Skeleton */
.skeleton-table-wrapper {
  @apply rounded-md bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden;
}

.skeleton-table-header {
  @apply bg-green-200 dark:bg-green-900/20 flex border-b border-green-900/30 dark:border-green-800;
}

.skeleton-header-cell {
  @apply flex-1 p-4 border-r border-green-900/20 last:border-r-0;
}

.skeleton-header-text {
  @apply h-4 bg-gradient-to-r from-green-700 via-green-600 to-green-700 rounded w-20;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-table-body {
  @apply divide-y divide-slate-200 dark:divide-slate-700;
}

.skeleton-table-row {
  @apply flex;
}

.skeleton-table-cell {
  @apply flex-1 p-3 border-r border-slate-200 dark:border-slate-700 last:border-r-0;
}

.skeleton-template-name {
  @apply flex items-center gap-2;
}

.skeleton-category {
  @apply flex items-center gap-2;
}

.skeleton-icon {
  @apply w-4 h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-text-primary {
  @apply h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded w-24;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-text-secondary {
  @apply h-3 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded w-16;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-badge {
  @apply h-6 bg-gradient-to-r from-green-200 via-green-300 to-green-200 dark:from-green-700 dark:via-green-600 dark:to-green-700 rounded-full w-20;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-actions {
  @apply flex items-center gap-2;
}

.skeleton-action-btn {
  @apply w-8 h-8 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

/* Pagination Skeleton */
.skeleton-pagination {
  @apply border-t border-green-100 dark:border-green-800 p-4 bg-green-200 dark:bg-green-900/10 flex items-center justify-between;
}

.skeleton-pagination-info {
  @apply flex-1;
}

.skeleton-text-info {
  @apply h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded w-48;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-pagination-controls {
  @apply flex gap-2;
}

.skeleton-pagination-btn {
  @apply h-8 w-16 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

/* Loading Status */
.skeleton-status {
  @apply flex items-center justify-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700;
}

.skeleton-pulse-dot {
  @apply w-2 h-2 bg-green-500 rounded-full;
  animation: pulse 1.5s infinite;
}

.skeleton-status-text {
  @apply text-sm text-slate-600 dark:text-slate-400 font-medium;
}

/* Templates Container */
.templates-container {
  @apply space-y-6 px-3 p-2;
}

.table-wrapper {
  @apply rounded-md bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden;
}

/* Table Styling */
.whatsapp-table {
  @apply w-full;
}

.whatsapp-table thead tr {
  @apply bg-green-200 dark:bg-green-900/20;
}

.whatsapp-table th {
  @apply font-semibold text-green-800 dark:text-green-300 p-4 text-left border-b border-green-900/30 dark:border-green-800;
}

.whatsapp-table td {
  @apply border-b border-green-900/30 dark:border-green-900/30;
}

.whatsapp-table tbody tr:last-child td {
  @apply border-b-0;
}

/* Table Cells */
.template-name {
  @apply flex items-center gap-2;
}

.template-text {
  @apply font-medium text-slate-800 dark:text-white;
}

.content-cell {
  @apply flex items-center;
}

.content-text {
  @apply text-sm;
}

.status-badge {
  @apply inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold;
}

.status-approved {
  @apply bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300;
}

.status-default {
  @apply bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300;
}

.actions-cell {
  @apply flex items-center gap-2;
}

/* Pagination */
.pagination-wrapper {
  @apply border border-slate-200 dark:border-slate-800 p-2 mb-2 rounded-md mt-4;
}

.pagination-container {
  @apply text-n-slate-10;
}

.pagination-controls {
  @apply flex items-center justify-between gap-4 flex-wrap;
}

:deep([data-radix-select-trigger]) {
  @apply bg-n-slate-1 border-n-slate-6 text-n-slate-12;
}

:deep([data-radix-select-trigger]:focus) {
  @apply ring-2 ring-green-500 border-green-500;
}

:deep([data-radix-select-content]) {
  @apply bg-n-slate-1 border-n-slate-6 shadow-lg rounded-lg p-1;
}

:deep([data-radix-select-item]) {
  @apply text-n-slate-12 bg-n-slate-1 cursor-pointer rounded px-3 py-2 mx-0.5;
}

:deep([data-radix-select-item]:hover) {
  @apply bg-n-slate-3;
}

:deep([data-radix-select-item][data-highlighted]) {
  @apply bg-n-slate-3 text-n-slate-12;
}

:deep(button:disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}

:deep(.pagination-controls button) {
  @apply border-n-slate-6 hover:bg-n-slate-3;
}

/* Empty State */
.empty-state-container {
  @apply flex justify-center items-center min-h-96;
}

.empty-state-card {
  @apply bg-white dark:bg-slate-800 rounded-2xl p-12 shadow-lg border border-green-100 dark:border-green-800 text-center max-w-md;
}

.empty-icon {
  @apply text-6xl mb-4;
}

.empty-title {
  @apply text-xl font-semibold text-slate-900 dark:text-white mb-2;
}

.empty-description {
  @apply text-slate-600 dark:text-slate-400 mb-6 leading-relaxed;
}

.empty-actions {
  @apply flex justify-center;
}

.btn-primary {
  @apply px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-none cursor-pointer;
}

/* Fade in animation */
.fade-in {
  animation: fadeIn 0.5s ease-in-out;
}

/* Keyframe animations */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .header-content {
    @apply flex-col gap-4 text-center;
  }

  .header-stats {
    @apply flex-wrap justify-center;
  }

  .stats-skeleton {
    @apply flex-wrap justify-center;
  }

  .actions-cell {
    @apply flex-col gap-1;
  }

  .whatsapp-icon {
    @apply w-8 h-8;
  }

  .pagination-container {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .skeleton-pagination {
    @apply flex-col gap-3 items-start;
  }

  .skeleton-table-row {
    @apply flex-col;
  }

  .skeleton-table-cell {
    @apply border-r-0 border-b border-slate-200 dark:border-slate-700 last:border-b-0;
  }
}

/* Skeleton animation delays */
.skeleton-table-row:nth-child(even) .skeleton-icon,
.skeleton-table-row:nth-child(even) .skeleton-text-primary,
.skeleton-table-row:nth-child(even) .skeleton-text-secondary,
.skeleton-table-row:nth-child(even) .skeleton-badge,
.skeleton-table-row:nth-child(even) .skeleton-action-btn {
  animation-delay: 0.2s;
}

.skeleton-table-row:nth-child(3n) .skeleton-icon,
.skeleton-table-row:nth-child(3n) .skeleton-text-primary,
.skeleton-table-row:nth-child(3n) .skeleton-text-secondary,
.skeleton-table-row:nth-child(3n) .skeleton-badge,
.skeleton-table-row:nth-child(3n) .skeleton-action-btn {
  animation-delay: 0.4s;
}

.btn-primary:focus {
  @apply outline-none ring-2 ring-green-500 ring-offset-2;
}
</style>
